import React, { useState, useEffect } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { Search, KeyboardArrowDown } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const FloatingSearchBar = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("Women");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  // Show after a slight delay
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = () => {
    if (search.trim().length > 0) {
      navigate("/search", { state: { search: search.trim(), gender } });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <Box
          component={motion.div}
          initial={{ y: 100, opacity: 0, x: "-50%" }}
          animate={{ y: 0, opacity: 1, x: "-50%" }}
          exit={{ y: 100, opacity: 0, x: "-50%" }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24, md: 16 },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
            maxWidth: "none",
            backgroundColor: "#1A1A1A",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              maxWidth: "none",
              boxSizing: "border-box",
              padding: { xs: "14px 12px", sm: "10px 16px", md: "14px 16px" },
            }}
          >
            {/* Left Search Icon */}
            <Search sx={{ display: { xs: "none", sm: "block" }, color: "#aaa", mr: { xs: 0.5, sm: 1 }, ml: { xs: 0, sm: 1 }, fontSize: { xs: "20px", sm: "24px" } }} />

            {/* Input Container with Custom Placeholder */}
            <Box sx={{ flex: 1, position: "relative", overflow: "hidden", display: "flex", alignItems: "center", marginRight: { xs: "8px", sm: "16px" } }}>
              {!search && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                    containerType: "inline-size",
                  }}
                >
                  <Box
                    sx={{
                      whiteSpace: "nowrap",
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: { xs: "13px", sm: "16px" },
                      fontFamily: "Poppins-Regular",
                      display: "inline-block",
                      "@keyframes marquee": {
                        "0%": { transform: "translateX(10px)" },
                        "50%": { transform: "translateX(calc(100cqw - 100% - 15px))" },
                        "100%": { transform: "translateX(10px)" },
                      },
                      animation: { xs: "marquee 8s linear infinite", sm: "none" }
                    }}
                  >
                    Search styles, outfits, trends across all brands...
                  </Box>
                </Box>
              )}

              <Box
                component="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder=""
                sx={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: { xs: "16px", sm: "18px" },
                  fontFamily: "Poppins-Regular",
                  color: "#fff",
                  height: "40px",
                }}
              />
            </Box>

            {/* Gender Selector */}
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                padding: "4px 10px",
                marginRight: { xs: "6px", sm: "16px" },
                border: "1px solid rgba(255, 255, 255, 0.4)",
                borderRadius: "20px",
                transition: "all 0.2s",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Typography sx={{ fontFamily: "Poppins-Medium", fontSize: { xs: "11px", sm: "15px" }, color: "#fff", textTransform: "uppercase" }}>
                {gender}
              </Typography>
              <KeyboardArrowDown sx={{ fontSize: { xs: "16px", sm: "22px" }, ml: 0.2, color: "#fff" }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              disableScrollLock={true}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
                  minWidth: "120px",
                  mb: 1,
                }
              }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {["Women", "Men", "Kids"].map((g) => (
                <MenuItem
                  key={g}
                  onClick={() => {
                    setGender(g);
                    setAnchorEl(null);
                  }}
                  selected={g === gender}
                  sx={{ fontFamily: "Poppins-Medium", fontSize: "13px" }}
                >
                  {g}
                </MenuItem>
              ))}
            </Menu>

            <Box
              onClick={handleSearch}
              sx={{
                order: { xs: -1, sm: 0 },
                marginRight: { xs: "12px", sm: 0 },
                backgroundColor: "#FF9B80",
                borderRadius: "12px",
                minWidth: { xs: "36px", sm: "52px", md: "100px" },
                width: { xs: "36px", sm: "52px", md: "100px" },
                height: { xs: "36px", sm: "52px", md: "40px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                transition: "background-color 0.2s",
                "&:hover": { backgroundColor: "#ff8a6a" },
              }}
            >
              <Search sx={{ color: "#fff", fontSize: { xs: "20px", sm: "26px" } }} />
            </Box>
          </Box>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default FloatingSearchBar;
